import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "How serverless fits side-projects perfectly",
  "description": "How do you provision servers for no load most days THEN ALL THE LOAD THIS MINUTE? That's how side-projects work and it's hard",
  "categories": "Serverless, Technical",
  "content_upgrade": "ServerlessHandbook",
  "published": "2021-03-10T00:00:00.000Z",
  "hero": "./img/screenshot-1615394225790.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`Friend, yesterday we talked about how a $12 AWS bill runs a $50k business. Today let's talk about `}<em parentName="p">{`why`}</em>{`.`}</p>
    <p>{`Think about the load on this newsletter builder:`}</p>
    <p><img parentName="p" {...{
        "src": "/26e495001d5d80e509efa11d5b2d32e9/xbHqpVgif6a75jc.gif",
        "alt": null
      }}></img></p>
    <p>{`How do you think it works?`}</p>
    <h2 {...{
      "id": "techletterapp-architecture"
    }}>{`TechLetter.app architecture`}</h2>
    <p>{`The frontend for TechLetterApp is a create-react-app project. Nothing fancy. You can `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/letter-builder"
      }}>{`see the code on GitHub`}</a>{`.`}</p>
    <p>{`It runs on Vercel, using their git integration. `}<inlineCode parentName="p">{`git push origin master`}</inlineCode>{` deploys new code in 2 minutes.`}</p>
    <p>{`On the left, a `}<inlineCode parentName="p">{`<textarea>`}</inlineCode>{` uses `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` callbacks to update values in global state. Shared via context in a cumbersome way because I was new to context 2 years ago. 😇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// MarkdownInput.js
export default () => (
  <Consumer>
    {({ markdown, onChangeMarkdown }) => (
      <React.Fragment>
        <Heading>Markdown</Heading>
        <Textarea onChange={onChangeMarkdown} value={markdown} />
      </React.Fragment>
    )}
  </Consumer>
)
`}</code></pre>
    <p><strong parentName="p">{`Every change triggers a re-render`}</strong>{` of the `}<inlineCode parentName="p">{`LetterRender`}</inlineCode>{` component. This detail is important.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// LetterRender.js
export default React.forwardRef((props, ref) => {
  const { markdown } = useContext(MarkdownContext)
  const rendered = useRemark(markdown)

  return (
    <div ref={ref}>
      <Heading>
        Rendered{" "}
        <span role="img" aria-label="face">
          {" "}
          💌
        </span>
      </Heading>
      <Content>{rendered}</Content>
    </div>
  )
})
`}</code></pre>
    <ContentUpgrades.ServerlessHandbook />
    <h3 {...{
      "id": "useremark-turns-markdown-into-react"
    }}>{`useRemark() turns markdown into React`}</h3>
    <p>{`See this line?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const rendered = useRemark(markdown)
`}</code></pre>
    <p>{`That's the frontend secret sauce.`}</p>
    <p>{`A compiler that takes Markdown and uses a chain of open source and `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/custom-markdown-extensions-with-remark-and-hast-handlers"
      }}>{`custom remark plugins`}</a>{`. One of the components it creates is called `}<inlineCode parentName="p">{`<Screenshot>`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Screenshot.js
function Screenshot({ node }) {
  const [image, setImage] = useState(loaderImg)

  const reloadImage = async () => {
    setImage(loaderImg)
    try {
      const res = await fetch(node.url),
        response = await res.json()

      if (response.status === "success") {
        setImage(response.url)
      } else {
        throw "Screenshot error"
      }
    } catch (e) {
      setImage(errorImg)
    }
  }

  useEffect(() => {
    reloadImage()
  }, [node])

  // display image
}
`}</code></pre>
    <p>{`Screenshot is important 👉 makes an API request `}<inlineCode parentName="p">{`const res = await fetch(node.url)`}</inlineCode>{` `}<em parentName="p">{`every time you sneeze`}</em>{`.`}</p>
    <p>{`Look at this:`}</p>
    <p><img parentName="p" {...{
        "src": "/365c5ead4d7721e9c1e62627fa945ae6/YlbnBOgif950176.gif",
        "alt": null
      }}></img></p>
    <p>{`soooooo many requests, every character triggers a new one 😂`}</p>
    <p>{`I knew it was bad, but I didn't realize it was `}<em parentName="p">{`that`}</em>{` bad until I made this gif.`}</p>
    <p>{`PS: it's because `}<inlineCode parentName="p">{`node`}</inlineCode>{` changes object identity on every change. Effect should run on `}<inlineCode parentName="p">{`node.url`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "a-traffic-pattern-that-kills-your-project"
    }}>{`A traffic pattern that kills your project`}</h2>
    <p>{`Every one of those requests talked to a server. The server had to:`}</p>
    <ol>
      <li parentName="ol">{`Run Chrome in headless mode without UI`}</li>
      <li parentName="ol">{`Load a `}<a parentName="li" {...{
          "href": "https://tweet-embedder-swizec.vercel.app/?url=https://twitter.com/Swizec/status/1367503207803609090"
        }}>{`special tweet embedder site`}</a>{` running on Vercel`}</li>
      <li parentName="ol">{`Wait for the page to load`}</li>
      <li parentName="ol">{`Find the right DOM node`}</li>
      <li parentName="ol">{`Measure its size`}</li>
      <li parentName="ol">{`Take a screenshot`}</li>
      <li parentName="ol">{`Save to a temporary file`}</li>
      <li parentName="ol">{`Upload that file to S3`}</li>
      <li parentName="ol">{`Return the S3 link`}</li>
    </ol>
    <p>{`Now imagine doing that 120 times while you type a sentence. Or 20 times `}<em parentName="p">{`all at once`}</em>{` when you paste a document.`}</p>
    <p>{`Server doesn't know you're bailing my friend, it finishes the full cycle every time. Yes my S3 is full of crap 😅`}</p>
    <p>{`What do you think traffic looks like?`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/14ea5d625e7724659588e5e728b73b73/ac307/Typical-traffic-pattern210a35.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "60.53811659192825%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'243\\'%20viewBox=\\'0%200%20400%20243\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M48%2012a1147%201147%200%2001-4%2095c-2%2021-3%2058-3%2082l1%2024H32c-12%201-11%203%201%202h9v5l2%2017%201-11-1-11%2063%201a2407%202407%200%2001192%204%20979%20979%200%200130%200l-64-2c-70-4-112-4-166-4H44l-1-8c-1-11%201-76%203-95l2-56c0-36%200-38%202-38%203%200%204-2%202-3s-2-2%200-3v-1l-4%202m174%205a4131%204131%200%2000-32%2020v-2c-1%200-8%2011-7%2012s7%200%2010-2c1-1%201-1-1-1l-7%201%207-6a475%20475%200%200030-22m125%200l-3%2016-2%2013a407%20407%200%2001-8%2061c-3%2013-10%2036-9%2029a873%20873%200%2001-3%204c0%206-3%2015-5%2021a241%20241%200%2000-9%2047l2-1%202-1h-1c-2%200-2-1%200-4%201-3%201-6-1-3v-2c1-9%208-35%209-34l-2%2011-2%2012%204-17c1-9%204-19%208-30%208-26%2010-36%2015-78l5-29c4-14%204-19%200-15M86%2034l-5%2021a637%20637%200%2000-10%20143v3l2-34A611%20611%200%200183%2066c-5%2026-5%2038-5%2080a518%20518%200%20012-4c0-51%200-59%205-81%202-13%203-34%201-27m90%2012a542%20542%200%2000-18%20108c-5%2025-5%2037%200%2038%202%200%203%200%203%202%201%202%201%202%201%200%200-3-1-4-3-4-4%200-4-11%200-31l6-37a676%20676%200%200111-76m78%2073c-4%204-8%2020-14%2057%200%206-2%2014-4%2018-2%206-2%209-1%209%201%201%201%201%202-1%200-1%201-2%203-1l6-1c3-1%203-1-2-1h-6l5-29c1-3%202-6%201-8-1-4%207-40%2010-42l-3%2018-3%2014%205-17c4-16%204-20%201-16\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/14ea5d625e7724659588e5e728b73b73/ca0a1/Typical-traffic-pattern210a35.webp 223w", "/static/14ea5d625e7724659588e5e728b73b73/75680/Typical-traffic-pattern210a35.webp 445w", "/static/14ea5d625e7724659588e5e728b73b73/8d1ba/Typical-traffic-pattern210a35.webp 890w", "/static/14ea5d625e7724659588e5e728b73b73/3838e/Typical-traffic-pattern210a35.webp 1335w", "/static/14ea5d625e7724659588e5e728b73b73/e11e5/Typical-traffic-pattern210a35.webp 1780w", "/static/14ea5d625e7724659588e5e728b73b73/b8ebc/Typical-traffic-pattern210a35.webp 2058w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/14ea5d625e7724659588e5e728b73b73/e92b6/Typical-traffic-pattern210a35.png 223w", "/static/14ea5d625e7724659588e5e728b73b73/e66bf/Typical-traffic-pattern210a35.png 445w", "/static/14ea5d625e7724659588e5e728b73b73/4ef49/Typical-traffic-pattern210a35.png 890w", "/static/14ea5d625e7724659588e5e728b73b73/4e814/Typical-traffic-pattern210a35.png 1335w", "/static/14ea5d625e7724659588e5e728b73b73/701e9/Typical-traffic-pattern210a35.png 1780w", "/static/14ea5d625e7724659588e5e728b73b73/ac307/Typical-traffic-pattern210a35.png 2058w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/14ea5d625e7724659588e5e728b73b73/4ef49/Typical-traffic-pattern210a35.png",
                "alt": "Typical traffic pattern",
                "title": "Typical traffic pattern",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Typical traffic pattern`}</figcaption>{`
  `}</figure></p>
    <p><strong parentName="p">{`Days of nothing`}</strong>{` followed by a `}<em parentName="p">{`huge spike`}</em>{` followed by lots of nothing. Your worst nightmare.`}</p>
    <p>{`How many servers do you set up? 🤔`}</p>
    <h2 {...{
      "id": "how-serverless-makes-this-work"
    }}>{`How serverless makes this work`}</h2>
    <p>{`Traffic like that is where serverless shines.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Google likes to call serverless architectures ‌from prototype to production to planet-scale.`}</p>
    </blockquote>
    <p>{`You don't want to use serverless at planet scale, but Google is right: Serverless scales. A lot.`}</p>
    <p>{`With serverless, you don't need to think about traffic patterns and provisioning. Average load? Peak load? Lowest load? Auto-scaling? Who cares 🤷‍♀️`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/slobodan_/status/1369679609655083020"
      }}>{`https://twitter.com/slobodan`}{`_`}{`/status/1369679609655083020`}</a></p>
    <p>{`At rest, serverless does nothing. No requests, no server, no cost.`}</p>
    <p>{`When ALL THE REQUESTS COME, serverless rallies the troops.`}</p>
    <p><img parentName="p" {...{
        "src": "/59123852bdc256941e72d6aae9425093/giphygifd9ci1j.gif",
        "alt": null
      }}></img></p>
    <p>{`Each request `}<strong parentName="p">{`creates a new server in milliseconds`}</strong>{`. Fastest I've seen was in the 20ms range for small functions. The `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/lambda-screenshot-as-a-service"
      }}>{`screenshot lambda`}</a>{` takes about half a second. Needs to run Chrome and Chrome is fat 🙃`}</p>
    <p>{`You get up to 1000 of these in parallel on AWS. Other providers are different.`}</p>
    <p>{`And I don't mean 1000 requests per second, oh no, I mean `}<em parentName="p">{`1000 servers all at once`}</em>{`. If your code takes 50ms to run, that's 50,000 requests per second. 🚀`}</p>
    <p>{`Speed depends on what you're doing. Screenshots are slow.`}</p>
    <h2 {...{
      "id": "what-about-traditional-servers"
    }}>{`What about traditional servers?`}</h2>
    <p>{`Now imagine TechLetterApp didn't have scale on demand ...`}</p>
    <p>{`You have 2 options:`}</p>
    <ol>
      <li parentName="ol">{`Servers for average load – 2 requests per day 🤨`}</li>
      <li parentName="ol">{`Servers for peak load – 200 requests per minute 💸`}</li>
    </ol>
    <p>{`Both suck.`}</p>
    <p>{`A newsletter builder that can do 2 screenshots per day is useless. Takes you a week to build 1 email.`}</p>
    <p>{`Scale to peak load of 200 per minute and you're throwing money in the furnace. Can you imagine the cost?`}</p>
    <p>{`You end up with a middle ground: Enough servers to work okay but not great.`}</p>
    <p>{`And that's how I kept crashing my friend's side project and nagging him on Slack every night while getting the Serverless Handbook print ready. He built a tool that takes markdown and turns it into a print PDF. Runs a render on every save.`}</p>
    <p>{`It crashed. A lot. Shit's hard 😅`}</p>
    <p>{`What do you think? Getting ideas yet? 😛 Hit reply`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: there's a chapter in Serverless Handbook about using serverless chrome, it's one of my favs`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      